import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import DashboardLayout from "@/components/lingo/DashboardLayout";
import api from "@/lib/api";
import {
  Headphones, BookOpen, PencilLine, Mic, CheckCircle2, Play, Clock,
  ChevronRight, Trophy, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const SECTION_ICON = { Listening: Headphones, Reading: BookOpen, Writing: PencilLine, Speaking: Mic };

const SESSION_KEY = (examId) => `lingoq_mock_session_${examId}`;

export default function MockExamRunner() {
  const { id } = useParams();
  const nav = useNavigate();
  const [exam, setExam] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [completedTestIds, setCompletedTestIds] = useState([]);
  const [sectionScores, setSectionScores] = useState({});

  useEffect(() => { (async () => {
    const r = await api.get(`/mock-exams/${id}`);
    setExam(r.data);
    // Restore or create a session
    const saved = localStorage.getItem(SESSION_KEY(id));
    if (saved) {
      const s = JSON.parse(saved);
      setSessionId(s.session_id);
      setCompletedTestIds(s.completed || []);
      setSectionScores(s.scores || {});
    } else {
      const s = await api.post("/mock-exams/sessions/start", { exam_id: id });
      setSessionId(s.data.id);
      localStorage.setItem(SESSION_KEY(id), JSON.stringify({ session_id: s.data.id, completed: [], scores: {} }));
    }
  })(); }, [id]);

  // Look for completed sub-test sessions for this user to mark sections done.
  // We do this by checking /tests/sessions/mine and matching test_ids.
  useEffect(() => {
    if (!exam || !sessionId) return;
    (async () => {
      const r = await api.get("/tests/sessions/mine");
      const completedForExam = r.data.filter((s) =>
        exam.sections.some((sec) => sec.test_id === s.test_id) && s.status === "COMPLETED"
      );
      const newCompleted = [...new Set([...completedTestIds, ...completedForExam.map((s) => s.test_id)])];
      const newScores = { ...sectionScores };
      for (const s of completedForExam) {
        if (!newScores[s.test_id] && s.score) {
          newScores[s.test_id] = s.score;
          // Record on backend
          try {
            await api.post("/mock-exams/sessions/record-section", {
              session_id: sessionId, test_id: s.test_id, score: s.score,
            });
          } catch {}
        }
      }
      if (newCompleted.length !== completedTestIds.length || JSON.stringify(newScores) !== JSON.stringify(sectionScores)) {
        setCompletedTestIds(newCompleted);
        setSectionScores(newScores);
        localStorage.setItem(SESSION_KEY(id), JSON.stringify({
          session_id: sessionId, completed: newCompleted, scores: newScores,
        }));
      }
    })();
    // eslint-disable-next-line
  }, [exam, sessionId]);

  if (!exam) return <DashboardLayout title="Loading..."><div className="text-slate-400">Preparing your mock exam…</div></DashboardLayout>;

  const totalSections = exam.sections.length;
  const doneCount = completedTestIds.length;
  const pct = Math.round((doneCount / totalSections) * 100);
  const allDone = doneCount === totalSections;

  const finish = async () => {
    try {
      const r = await api.post(`/mock-exams/sessions/${sessionId}/finish`);
      toast.success(`Mock complete — band ${r.data.overall_band ?? "—"}`);
      localStorage.removeItem(SESSION_KEY(id));
      nav("/app/mock");
    } catch (e) { toast.error("Could not finalise mock"); }
  };

  return (
    <DashboardLayout hideHeader>
      {/* Header */}
      <div className="flex items-center justify-between pb-3 border-b border-[#F77F00]/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[#F77F00] text-white flex items-center justify-center"><Trophy className="w-4 h-4" /></div>
          <h1 data-testid="page-title" className="text-[#F77F00] font-display font-semibold text-lg">{exam.title}</h1>
        </div>
        <div className="text-sm text-slate-500 flex items-center gap-2"><Clock className="w-4 h-4" /> {exam.duration_minutes} min total</div>
      </div>

      {/* Progress */}
      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="kicker">Your Progress</div>
            <div className="font-display text-2xl text-[#003049] mt-1">{doneCount} of {totalSections} sections complete</div>
          </div>
          <div className="font-display text-4xl text-[#F77F00]">{pct}%</div>
        </div>
        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#003049] to-[#F77F00] rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Sections */}
      <div className="mt-6 space-y-3">
        {exam.sections.map((sec, i) => {
          const Icon = SECTION_ICON[sec.label] || Headphones;
          const done = completedTestIds.includes(sec.test_id);
          const score = sectionScores[sec.test_id];
          const locked = i > 0 && !completedTestIds.includes(exam.sections[i - 1].test_id) && !done;
          return (
            <div key={sec.order} data-testid={`section-${sec.label}`}
              className={`flex items-center gap-4 p-5 rounded-2xl border ${done ? "bg-emerald-50 border-emerald-200" : locked ? "bg-slate-50 border-slate-200 opacity-60" : "bg-white border-slate-200 hover:border-[#F77F00]"}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${done ? "bg-emerald-500 text-white" : locked ? "bg-slate-200 text-slate-400" : "bg-[#F77F00] text-white"}`}>
                {done ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-slate-500 font-mono uppercase tracking-widest">Section {sec.order} · {sec.label}</div>
                <div className="font-medium text-[#003049] truncate">{sec.test?.title || sec.test_id}</div>
                {done && score && (
                  <div className="text-xs text-emerald-700 mt-1">
                    Score: <span className="font-mono font-semibold">
                      {score.band ?? `${score.correct}/${score.total}`}
                    </span>
                  </div>
                )}
              </div>
              {done ? (
                <span className="px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">Completed</span>
              ) : locked ? (
                <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-500 text-xs">Locked</span>
              ) : (
                <Link to={`/app/tests/${sec.test_id}`} data-testid={`start-section-${sec.label}`}
                  className="px-4 py-2 rounded-full bg-[#F77F00] hover:bg-[#D26900] text-white text-sm font-medium inline-flex items-center gap-1.5">
                  <Play className="w-3.5 h-3.5 fill-white" /> Start
                </Link>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 flex items-center justify-between">
        <Link to="/app/mock" className="text-sm text-slate-500 hover:text-[#003049]">← Back to all mocks</Link>
        <button onClick={finish} disabled={!allDone} data-testid="finish-mock"
          className="px-6 py-3 rounded-full bg-[#003049] hover:bg-[#00263A] text-white font-medium inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed">
          {allDone ? "Submit final band" : `Finish all sections first (${doneCount}/${totalSections})`} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </DashboardLayout>
  );
}
